import { create } from 'zustand'
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp
} from 'firebase/firestore'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { derived } from '@/lib/storeHelpers'
import {
  FALLBACK_LEAGUE_PORTALS,
  mergeLeaguePortal,
  setLiveLeaguePortals,
  slugifyLeagueId,
  getAllLeaguePortals,
  getVisibleLeaguePortals
} from '@/config/leaguePortals'

type Result = { success: boolean; error?: string; [key: string]: any }

export interface LeaguePortalsState {
  portals: any[]
  loaded: boolean
  error: string | null
  // derived
  visiblePortals: any[]
  // actions
  subscribe: () => void
  stop: () => void
  waitUntilLoaded: (timeoutMs?: number) => Promise<void>
  savePortalBranding: (leagueId: string, fields: any) => Promise<Result>
  createPortal: (data: {
    id: string
    displayName?: string
    subtitle?: string
    accentColor?: string
    sponsorAbbr?: string
  }) => Promise<Result>
  uploadLogo: (leagueId: string, file: File | null | undefined) => Promise<Result>
  getAllLeaguePortals: typeof getAllLeaguePortals
  getVisibleLeaguePortals: typeof getVisibleLeaguePortals
}

let unsub: null | (() => void) = null

export const useLeaguePortalsStore = create<LeaguePortalsState>()(
  derived<LeaguePortalsState>((s) => ({
    visiblePortals: s.portals.filter((p) => p.isVisible !== false)
  }))((set, get) => ({
    portals: FALLBACK_LEAGUE_PORTALS.map((p) => ({ ...p })),
    loaded: false,
    error: null,
    visiblePortals: [],

    subscribe: () => {
      if (unsub) return
      unsub = onSnapshot(
        collection(db, 'publicLeagues'),
        (snap) => {
          const fromFs = snap.docs.map((d) => mergeLeaguePortal(d.id, d.data() || {}))
          const seen = new Set(fromFs.map((p) => p.id))
          // Keep compiled fallbacks if those league docs are missing (emulator / first load).
          const merged = [
            ...fromFs,
            ...FALLBACK_LEAGUE_PORTALS.filter((p) => !seen.has(p.id)).map((p) => ({ ...p }))
          ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          set({ portals: merged })
          setLiveLeaguePortals(merged)
          set({ loaded: true, error: null })
        },
        (err) => {
          console.error('leaguePortals subscribe', err)
          set({ error: err.message, portals: FALLBACK_LEAGUE_PORTALS.map((p) => ({ ...p })) })
          setLiveLeaguePortals(get().portals)
          set({ loaded: true })
        }
      )
    },

    stop: () => {
      if (unsub) {
        unsub()
        unsub = null
      }
    },

    savePortalBranding: async (leagueId, fields) => {
      const authStore = useAuthStore.getState()
      if (!authStore.isSuperAdmin) {
        return { success: false, error: 'Super admin only' }
      }
      const id = String(leagueId || '').trim()
      if (!id) return { success: false, error: 'Missing league id' }
      try {
        const payload: Record<string, any> = {
          displayName: String(fields.displayName || '').trim(),
          subtitle: String(fields.subtitle || '').trim(),
          accentColor: String(fields.accentColor || '').trim() || '#1a237e',
          sponsorAbbr: String(fields.sponsorAbbr || '').trim(),
          isVisible: fields.isVisible !== false,
          order: Number(fields.order) || 0,
          updatedAt: serverTimestamp()
        }
        if (fields.logoPath != null) {
          payload.logoPath = String(fields.logoPath || '').trim()
        }
        await setDoc(doc(db, 'publicLeagues', id), payload, { merge: true })
        return { success: true }
      } catch (e) {
        return { success: false, error: e.message }
      }
    },

    waitUntilLoaded: (timeoutMs = 4000) => {
      get().subscribe()
      if (get().loaded) return Promise.resolve()
      return new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, timeoutMs)
        // watch(loaded, ...)
        const stop = useLeaguePortalsStore.subscribe((state, prev) => {
          if (state.loaded === prev.loaded) return
          if (!state.loaded) return
          clearTimeout(timer)
          stop()
          resolve()
        })
      })
    },

    createPortal: async ({ id, displayName, subtitle, accentColor, sponsorAbbr }) => {
      const authStore = useAuthStore.getState()
      if (!authStore.isSuperAdmin) {
        return { success: false, error: 'Super admin only' }
      }
      const slug = slugifyLeagueId(id)
      if (!slug) return { success: false, error: 'Enter a league id (letters and numbers)' }
      const portals = get().portals
      if (portals.some((p) => p.id === slug)) {
        return { success: false, error: `League "${slug}" already exists` }
      }
      const nextOrder = Math.max(0, ...portals.map((p) => p.order || 0)) + 1
      try {
        await setDoc(doc(db, 'publicLeagues', slug), {
          displayName: String(displayName || slug).trim(),
          subtitle: String(subtitle || 'Schedule, standings and stats').trim(),
          accentColor: String(accentColor || '#1a237e').trim(),
          sponsorAbbr: String(sponsorAbbr || slug.toUpperCase()).trim(),
          logoPath: '',
          isVisible: true,
          order: nextOrder,
          currentSeasonId: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return { success: true, leagueId: slug }
      } catch (e) {
        return { success: false, error: e.message }
      }
    },

    uploadLogo: async (leagueId, file) => {
      const authStore = useAuthStore.getState()
      if (!authStore.isSuperAdmin || !authStore.user) {
        return { success: false, error: 'Super admin only' }
      }
      if (!file) return { success: false, error: 'Choose an image' }
      const uid = authStore.user.uid
      const safeName = String(file.name || 'logo.png').replace(/[^\w.\-]+/g, '_')
      const path = `league-logos/${leagueId}/${uid}/${Date.now()}_${safeName}`
      try {
        const ref = storageRef(getStorage(), path)
        await uploadBytes(ref, file, { contentType: file.type || 'image/png' })
        const logoPath = await getDownloadURL(ref)
        await setDoc(
          doc(db, 'publicLeagues', leagueId),
          { logoPath, updatedAt: serverTimestamp() },
          { merge: true }
        )
        return { success: true, logoPath }
      } catch (e) {
        return { success: false, error: e.message }
      }
    },

    getAllLeaguePortals,
    getVisibleLeaguePortals
  }))
)
